/* eslint-disable import/extensions */
import { useDispatch } from 'react-redux';
import { SearchParamsArrearage } from '@pages/arrearage/Arrearage';
import { baseErrorHandler } from '@queries/baseErrorHandler';
import { showMessage } from '@queries/showMessage';
import { setActionCollection, setCollectionMediaFile } from '@reducers/roboto-call-slice';
import { $api } from '@service/RequestService';
import { getDebitsBody } from '@utils/helper/getSearchAndDownloadBodyAllTables';
import { useMutation, useQueryClient } from 'react-query';

import { useUpdateNode } from 'modules/ai-agent/hooks';
import { setAudio } from 'modules/ai-agent/store';

import { endPoints } from '../utils/constants/endPoints';
import { queryKeys } from '../utils/constants/queryKeys';
import { CollectionMediaFileType } from '../utils/models/call-collection';
import { CampagingCreateModel } from '../utils/models/CampagingCreateModel';
import { SipModel } from '../utils/models/SipModel';

export function useCreateAndUpdateSip() {
  const client = useQueryClient();

  return useMutation(
    async (data: SipModel) => {
      const res = await $api.post(endPoints.CREATE_SIP, data);

      return res.data;
    },
    {
      onSuccess: data => {
        client.invalidateQueries([queryKeys.ROBOTO_SIP]);
        showMessage(data.message, data.accept);
      },
      onError: baseErrorHandler
    }
  );
}

export function useDeleteSip() {
  const client = useQueryClient();

  return useMutation(
    async (id: number) => {
      const res = await $api.delete(`${endPoints.CREATE_SIP}/${id}`);

      return res.data;
    },
    {
      onSuccess: data => {
        client.invalidateQueries([queryKeys.ROBOTO_SIP]);
        showMessage(data.message, data.accept);
      },
      onError: baseErrorHandler
    }
  );
}

export function useCreateCampaging() {
  const client = useQueryClient();

  return useMutation(
    async (data: CampagingCreateModel) => {
      const res = await $api.post(`${endPoints.ONE_CAMPAGING}`, { ...data });

      return res.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: [queryKeys.CAMPAGING_ALL] });
      }
    }
  );
}

export function useCreateRobotoDebt(searchParams: SearchParamsArrearage) {
  const client = useQueryClient();

  const paramCopy = { ...getDebitsBody(searchParams) };

  delete paramCopy.page;
  delete paramCopy.size;

  return useMutation(
    async (data: CampagingCreateModel) => {
      const res = await $api.post(`${endPoints.CREATE_ROBOTO_DEBT}`, { ...data, ...paramCopy });

      return res.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: [queryKeys.CAMPAGING_ALL] });
      }
    }
  );
}

export function useDeleteCampaign() {
  const client = useQueryClient();

  return useMutation(
    async (id: number) => {
      const res = await $api.delete(`${endPoints.ONE_CAMPAGING}/${id}`);

      return res.data;
    },
    {
      onSuccess: async data => {
        await client.invalidateQueries({ queryKey: [queryKeys.CAMPAGING_ALL] });

        showMessage(data.message, data.accept);
      },
      onError: baseErrorHandler
    }
  );
}

export function useUploadMedia(type?: 'ai_agent') {
  const dispatch = useDispatch();
  const { updateJsonField } = useUpdateNode();

  const { data, ...arg } = useMutation<CollectionMediaFileType[], unknown, FormData>(
    async payload => {
      const { data } = await $api.post('/v1/files', payload);

      return data?.data;
    },
    {
      onSuccess: file => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        if (type) {
          dispatch(setAudio({ file: file?.[0] }));
          updateJsonField('fileId', file?.[0]?.id);
        } else {
          dispatch(setCollectionMediaFile({ files: file?.[0] }));
        }
      },
      onError: baseErrorHandler
    }
  );

  return { data, ...arg };
}

export function useCreateOrUpdateCollection() {
  const qc = useQueryClient();
  const dispatch = useDispatch();

  const { data, ...arg } = useMutation(
    async (payload: any) => {
      const { data } = await $api.post('/v1/robot-call/campaign', payload);

      return { data };
    },
    {
      onSuccess: async data => {
        await qc.invalidateQueries([queryKeys.CAMPAGING_ALL]);

        showMessage(data.data?.message, data.data?.accept);
      },
      onSettled: () => {
        dispatch(
          setActionCollection({
            visible: false
          })
        );
      }
    }
  );

  return { ...arg };
}

export function useCancelCampaign() {
  const qc = useQueryClient();

  return useMutation(
    async (id: number) => {
      const res = await $api.patch(`v1/robot-call/campaign/cancel/${id}`);

      return res.data;
    },
    {
      onSuccess: async data => {
        await qc.invalidateQueries({ queryKey: [queryKeys.CAMPAGING_ALL] });

        showMessage(data.message, data.accept);
      },
      onError: baseErrorHandler
    }
  );
}

export function useResumeCampaign() {
  const qc = useQueryClient();

  return useMutation(
    async (id: number) => {
      const res = await $api.patch(`/v1/robot-call/campaign/resume/${id}`);

      return res.data;
    },
    {
      onSuccess: async data => {
        await qc.invalidateQueries({ queryKey: [queryKeys.CAMPAGING_ALL] });

        showMessage(data.message, data.accept);
      },
      onError: baseErrorHandler
    }
  );
}

export function usePauseCampaign() {
  const qc = useQueryClient();

  return useMutation(
    async (id: number) => {
      const res = await $api.patch(`/v1/robot-call/campaign/pause/${id}`);

      return res.data;
    },
    {
      onSuccess: async data => {
        await qc.invalidateQueries({ queryKey: [queryKeys.CAMPAGING_ALL] });

        showMessage(data.message, data.accept);
      },
      onError: baseErrorHandler
    }
  );
}
